import React from 'react';
import {
  Box,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Button,
} from '@material-ui/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styles from '../forgotPassword.module.scss';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';

import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CancelIcon from '@material-ui/icons/Cancel';

const ConfirmCodeForm = ({onResetFormSubmit, resetPwdData, isLoading, setResetPwdData}) => {
    
    const [validPassword, setValidPassword] = React.useState(false);
    const [passwordErrorMsg, setPasswordErrorMsg] = React.useState(null);

    const [showPassword, setShowPassword] = React.useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

    const handleClickShowPassword = () => setShowPassword(!showPassword);
    const handleMouseDownPassword = () => setShowPassword(!showPassword);

    const handleClickShowConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);
    const handleMouseDownConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

    function checkSequentialString(s) {
        // Check for sequential numerical characters
        for(let i in s) 
            if (+s[+i+1] == +s[i]+1 && 
                +s[+i+2] == +s[i]+2) return false;
        // Check for sequential alphabetical characters
        for(let i in s) 
            if (String.fromCharCode(s.charCodeAt(i)+1) == s[+i+1] && 
                String.fromCharCode(s.charCodeAt(i)+2) == s[+i+2]) return false;
        return true;
    }

    const resetPwdSchema = Yup.object().shape({
        username: Yup.string().required('This field is required'),
        passwd: Yup.string()
        .max(25, 'Password must not be longer than 25 characters')
        .required('This field is required')
        .test('passwordStrengthCheck', 'The password does not meet minimum criteria', 
          function(value) {
            const pwdRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{12,})/;
    
            let errorMsg = '';
    
            if (!(/^(?=.*[a-z])(?=.*[A-Z])/.test(value))) {
              errorMsg += "Must have atleast one upper and lower case character \n"
            }
            if (!(/^(?=.*[0-9])/.test(value))) {
              errorMsg += "Must have atleast one numeric character \n"
            }
            if (!(/^(?=.*[!@#\$%\^&\*])/.test(value))) {
              errorMsg += "Must have atleast one special character \n"
            }
    
            if (!checkSequentialString(value)) {
              errorMsg += "Must not have alpha or numeric sequences \n"
            }
    
            if (!(/^(?=.{12,})/.test(value))) {
              errorMsg += "Must have 12 characters \n"
            }
    
            setPasswordErrorMsg(errorMsg);
    
            let isValidPassword = pwdRegex.test(value) && checkSequentialString(value);
    
            setValidPassword(isValidPassword);
    
            return isValidPassword;
          }),
        passwd_confirmation: Yup.string()
          .required('This field is required')
          .oneOf([Yup.ref('passwd'), null], 'Passwords do not match'),
        code: Yup.string().required('This field is required'),
    });

    const formikResetPass = useFormik({
        initialValues: resetPwdData,
        validationSchema: resetPwdSchema,
        onSubmit: onResetFormSubmit,
    });


    return (
        <Box
            component="form"
            noValidate
            onSubmit={formikResetPass.handleSubmit}
            data-testid="reset-pwd-form"
        >
            <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            >
                <Grid item>                    
                    <TextField
                        margin="normal"
                        variant="outlined"
                        required
                        fullWidth
                        id="code"
                        label="Code"
                        name="code"
                        autoComplete="code"
                        autoFocus
                        disabled={isLoading}
                        value={formikResetPass.values.code}
                        onChange={formikResetPass.handleChange}
                        error={formikResetPass.touched.username && Boolean(formikResetPass.errors.code)}
                        helperText={formikResetPass.touched.code && formikResetPass.errors.code}
                    />
                    <TextField
                        variant="outlined"
                        margin="normal"
                        value={formikResetPass.values.passwd}
                        disabled={isLoading}
                        required
                        fullWidth
                        autoComplete="new-passwd"
                        onChange={formikResetPass.handleChange}
                        onBlur={formikResetPass.handleBlur}
                        error={formikResetPass.touched.passwd && Boolean(formikResetPass.errors.passwd)}
                        helperText={
                            formikResetPass.errors.passwd && formikResetPass.touched.passwd && formikResetPass.errors.passwd
                        }
                        name="passwd"
                        label="Password"
                        type={showPassword ? "text" : "password"} 
                        id="passwd"
                        InputProps={{
                            endAdornment:
                            (
                            <>
                                {formikResetPass.touched.passwd && validPassword && (<CheckCircleIcon style={{color:'green'}} />)}
                                {formikResetPass.touched.passwd && !validPassword && (<Tooltip title={<p style={{margin: '0px 0px 0px 0px', padding: '10px 10px 10px 10px'}}><span style={{ whiteSpace: 'pre-line', cursor: 'pointer'}}>{passwordErrorMsg}</span></p>}><CancelIcon style={{color:'red', cursor: 'pointer'}} /></Tooltip>)}

                                <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                >
                                {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            </>
                            
                            )
                        }}
                    />
                    <TextField
                        variant="outlined"
                        margin="normal"
                        value={formikResetPass.values.passwd_confirmation}
                        required
                        disabled={isLoading}
                        fullWidth
                        autoComplete="new-password"
                        onChange={formikResetPass.handleChange}
                        onBlur={formikResetPass.handleBlur}
                        error={formikResetPass.touched.passwd_confirmation && Boolean(formikResetPass.errors.passwd_confirmation)}
                        helperText={
                            formikResetPass.errors.passwd_confirmation &&
                            formikResetPass.touched.passwd_confirmation &&
                            formikResetPass.errors.passwd_confirmation
                        }
                        name="passwd_confirmation"
                        label="Password Confirmation"
                        type={showConfirmPassword ? "text" : "password"} 
                        id="passwd_confirmation"
                        
                        InputProps={{ // <-- This is where the toggle button is added.
                            endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                aria-label="toggle password visibility"
                                onClick={handleClickShowConfirmPassword}
                                onMouseDown={handleMouseDownConfirmPassword}
                                >
                                {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            </InputAdornment>
                            )
                        }}
                    />                    
                </Grid>
            </Grid>
            <Grid container spacing={0} alignItems="center" justifyContent="center">
                <Grid item xs={8}>
                    <Box sx={{ m: 1.5 }}>
                        <Button
                            type="submit"
                            loading={isLoading}
                            variant="contained"
                            fullWidth
                            sx={{ mt: 2 }}
                            color="primary"
                            className={`${styles['login-btn']}`}
                            disabled={isLoading}
                        >
                            <Box data-testid="login-submit" sx={{fontWeight: 'bold'}}>Reset Password</Box>
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )
}

export default ConfirmCodeForm;